<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useDateLocale } from 'dashboard/composables/useTimeAgo';

const { t } = useI18n();
const { localeTag } = useDateLocale();

const props = defineProps({
  report: { type: Object, default: null },
  // eslint-disable-next-line vue/no-unused-properties
  flowId: { type: [String, Number], required: true },
  loading: { type: Boolean, default: false },
});

const percentFormatter = computed(() =>
  new Intl.NumberFormat(localeTag.value, {
    style: 'percent',
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })
);
const currencyFormatter = computed(() =>
  new Intl.NumberFormat(localeTag.value, { style: 'currency', currency: 'BRL' })
);
const numberFormatter = computed(() => new Intl.NumberFormat(localeTag.value));

const cards = computed(() => [
  {
    key: 'revenue',
    label: t('FLOW_BUILDER.REPORTS.CONVERSION.REVENUE'),
    value: currencyFormatter.value.format(
      Number(props.report?.orders_revenue?.total) || 0
    ),
  },
  {
    key: 'orders',
    label: t('FLOW_BUILDER.REPORTS.CONVERSION.ORDERS'),
    value: numberFormatter.value.format(props.report?.orders_count?.total ?? 0),
  },
  {
    key: 'conversion',
    label: t('FLOW_BUILDER.REPORTS.CONVERSION.RATE'),
    value: percentFormatter.value.format(props.report?.rates?.conversion ?? 0),
  },
]);
</script>

<template>
  <div class="flex flex-col gap-6">
    <div class="grid grid-cols-1 sm:grid-cols-3 gap-3">
      <template v-if="loading">
        <div
          v-for="n in 3"
          :key="`conversion-skeleton-${n}`"
          class="flex flex-col gap-2 border rounded-lg border-n-slate-4 bg-n-solid-1 p-4 animate-pulse"
        >
          <div class="h-3 w-28 rounded bg-n-alpha-2" />
          <div class="h-7 w-24 rounded bg-n-alpha-2 mt-0.5" />
        </div>
      </template>
      <template v-else>
        <div
          v-for="card in cards"
          :key="card.key"
          class="flex flex-col gap-1 border rounded-lg border-n-slate-4 bg-n-solid-1 p-4"
        >
          <span
            class="text-xs font-semibold uppercase tracking-wide text-n-slate-11"
          >
            {{ card.label }}
          </span>
          <span class="text-2xl font-semibold text-n-slate-12 tabular-nums">
            {{ card.value }}
          </span>
        </div>
      </template>
    </div>
  </div>
</template>
